var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import PropTypes from 'prop-types';
import cancelablePromise from '../cancelable-promise';
import { ThemedMedia } from './Media';
export var getSourceFromRatio = function (images) {
    if (typeof window === 'undefined') {
        return images.x1;
    }
    var cappedRatio = Math.min(3, Math.max(1, Math.ceil(window.devicePixelRatio)));
    return images["x" + cappedRatio] || images.x1;
};
var preloadImage = function (src) {
    return new Promise(function (resolve, reject) {
        var img = document.createElement('img');
        img.onload = function () {
            img.onload = null;
            img.onerror = null;
            resolve(src);
        };
        img.onerror = function (err) {
            img.onload = null;
            img.onerror = null;
            reject(err);
        };
        img.src = src;
    });
};
export var ImagePropTypes = {
    /** Image with 1x pixel density */
    x1: PropTypes.string,
    /** Image with 2x pixel density (will fall back to x1 is missing) */
    x2: PropTypes.string,
    /** Image with 3x pixel density (will fall back to x1 is missing) */
    x3: PropTypes.string,
};
var LazyImage = /** @class */ (function (_super) {
    __extends(LazyImage, _super);
    function LazyImage(props) {
        var _this = _super.call(this, props) || this;
        _this.promise = null;
        _this.state = { thumbnail: props.thumbnail, loaded: false, image: null };
        return _this;
    }
    LazyImage.prototype.loadImage = function () {
        var _this = this;
        if (this.promise) {
            this.promise.cancel();
        }
        var _a = this.props, x1 = _a.x1, x2 = _a.x2, x3 = _a.x3, preload = _a.preloadImage;
        var src = getSourceFromRatio({ x1: x1, x2: x2, x3: x3 });
        this.promise = cancelablePromise(function (resolve, reject, onCancel) {
            onCancel.shouldReject = false;
            var cancelled = false;
            onCancel(function () {
                cancelled = true;
            });
            preload(src)
                .then(function (image) {
                if (!cancelled) {
                    _this.setState({ loaded: true, image: image });
                    resolve();
                }
            })
                .catch(function (err) {
                // tslint:disable-next-line no-console
                console.warn(err.message);
                reject(err);
            });
        });
        this.promise.catch(function () {
            // Ignore cancelation errors
        });
    };
    LazyImage.prototype.componentDidMount = function () {
        this.loadImage();
    };
    LazyImage.prototype.componentDidUpdate = function (prevProps) {
        // If the image changed, load the new one
        if (this.props.x1 !== prevProps.x1 ||
            this.props.x2 !== prevProps.x2 ||
            this.props.x3 !== prevProps.x3) {
            this.loadImage();
        }
    };
    LazyImage.prototype.componentWillUnmount = function () {
        if (this.promise) {
            this.promise.cancel();
        }
    };
    LazyImage.prototype.render = function () {
        return this.props.children(this.state);
    };
    LazyImage.propTypes = __assign({ 
        /** Render function that will get state of the component as a first argument */
        children: PropTypes.func.isRequired, 
        /** Small, thumbnail image that will be rendered while the big one is loading */
        thumbnail: PropTypes.string.isRequired }, ImagePropTypes);
    LazyImage.defaultProps = {
        preloadImage: preloadImage,
    };
    return LazyImage;
}(React.Component));
export default LazyImage;
export var ResponsiveLazyImage = function (_a) {
    var x1 = _a.x1, x2 = _a.x2, x3 = _a.x3, mobile = _a.mobile, tablet = _a.tablet, desktop = _a.desktop, large_desktop = _a.large_desktop, thumbnail = _a.thumbnail, children = _a.children, preload = _a.preloadImage;
    if (x1 && x2 && x3) {
        // Support for old responsive image format
        // that was working only for dpr
        return (React.createElement(LazyImage, { x1: x1, x2: x2, x3: x3, thumbnail: thumbnail, preloadImage: preload }, children));
    }
    else if (mobile && tablet && desktop) {
        // New responsive image format that supports
        // both dpr and main breakpoints
        return (React.createElement(ThemedMedia, null, function (matches) {
            var imageProps = matches.large_desktop && large_desktop
                ? large_desktop
                : matches.desktop
                    ? desktop
                    : matches.tablet
                        ? tablet
                        : mobile;
            return (React.createElement(LazyImage, __assign({ thumbnail: thumbnail }, imageProps, { preloadImage: preload }), children));
        }));
    }
    else {
        // tslint:disable-next-line no-console
        console.warn('<ResponsiveLazyImage /> is missing required props');
        return null;
    }
};
export var ResponsiveLazyImagePropsShape = __assign({ 
    /** Mobile image config. */
    mobile: PropTypes.shape(ImagePropTypes), 
    /** Tablet image config. */
    tablet: PropTypes.shape(ImagePropTypes), 
    /** Desktop image config. */
    desktop: PropTypes.shape(ImagePropTypes), 
    /** Large Desktop image config. */
    large_desktop: PropTypes.shape(ImagePropTypes), 
    /** Image thumbnail. */
    thumbnail: PropTypes.string.isRequired }, ImagePropTypes);
ResponsiveLazyImage.propTypes = __assign({}, ResponsiveLazyImagePropsShape, { children: PropTypes.func.isRequired });
